import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, Table, TableHead, TableBody, TableRow, TableCell, IconButton,
  Chip, Alert, Snackbar, MenuItem, Tooltip, CircularProgress, Stack, useTheme, alpha,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, ContentCopy as CopyIcon,
  VpnKey as KeyIcon, CloudSync as SyncIcon, Mail as MailIcon, Send as SendIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import api from '../../utils/api';

interface AppPassword {
  id: string;
  label: string;
  scope: string;
  hint: string;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  revokedAt: string | null;
  createdAt: string;
}

const SCOPE_META: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  webdav: { label: 'DriveX (WebDAV)', icon: <SyncIcon fontSize="small" />, color: '#6366f1' },
  imap: { label: 'IMAP', icon: <MailIcon fontSize="small" />, color: '#059669' },
  smtp: { label: 'SMTP', icon: <SendIcon fontSize="small" />, color: '#f97316' },
  all: { label: 'All protocols', icon: <VerifiedIcon fontSize="small" />, color: '#dc2626' },
};

const formatDate = (s: string | null): string => {
  if (!s) return 'Never';
  const d = new Date(s);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
};

const AppPasswordsSettings: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [items, setItems] = useState<AppPassword[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newScope, setNewScope] = useState('webdav');
  const [creating, setCreating] = useState(false);
  const [createdPassword, setCreatedPassword] = useState<{ password: string; label: string } | null>(null);
  const [snack, setSnack] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [revokeId, setRevokeId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/users/me/app-passwords');
      const data = res.data?.data ?? res.data;
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setSnack({ msg: 'Failed to load app-passwords', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newLabel.trim()) {
      setSnack({ msg: 'Please enter a label (e.g. "My laptop")', severity: 'error' });
      return;
    }
    setCreating(true);
    try {
      const res = await api.post('/users/me/app-passwords', { label: newLabel.trim(), scope: newScope });
      const data = res.data?.data ?? res.data;
      setCreatedPassword({ password: data.password, label: data.label });
      setNewLabel('');
      setNewScope('webdav');
      setCreateOpen(false);
      await load();
    } catch (e: any) {
      setSnack({ msg: e?.response?.data?.message || 'Failed to create app-password', severity: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: string) => {
    try {
      await api.delete(`/users/me/app-passwords/${id}`);
      setSnack({ msg: 'App-password revoked', severity: 'success' });
      setRevokeId(null);
      await load();
    } catch (e: any) {
      setSnack({ msg: e?.response?.data?.message || 'Failed to revoke', severity: 'error' });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnack({ msg: 'Copied to clipboard', severity: 'success' });
  };

  const active = items.filter(i => !i.revokedAt);
  const revoked = items.filter(i => i.revokedAt);

  return (
    <Box sx={{ maxWidth: 920, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <KeyIcon sx={{ fontSize: 32, color: '#6366f1' }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>App Passwords</Typography>
          <Typography variant="body2" color="text.secondary">
            Per-device passwords for desktop sync clients (DriveX-Sync), IMAP/SMTP apps, and WebDAV.
          </Typography>
        </Box>
      </Stack>

      <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
        <Typography variant="body2">
          <strong>Use these instead of your account password</strong> for desktop apps and 3rd-party clients.
          Each password is scoped, revocable, and tracks last-used time/IP. The plaintext is shown
          <strong> only once</strong> — copy it immediately to your client.
        </Typography>
      </Alert>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Active ({active.length})
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New app password
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
        ) : active.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <KeyIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No app passwords yet. Create one to use DriveX-Sync or IMAP/SMTP clients.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Label</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Hint</TableCell>
                <TableCell>Last used</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {active.map(p => {
                const meta = SCOPE_META[p.scope] || SCOPE_META.webdav;
                return (
                  <TableRow key={p.id} hover>
                    <TableCell><strong>{p.label}</strong></TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={meta.icon as any}
                        label={meta.label}
                        sx={{ bgcolor: alpha(meta.color, 0.12), color: meta.color, fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell><code style={{ fontSize: 12 }}>{p.hint}</code></TableCell>
                    <TableCell>
                      <Tooltip title={p.lastUsedAt ? `${p.lastUsedAt} from ${p.lastUsedIp || 'unknown'}` : ''}>
                        <span>{formatDate(p.lastUsedAt)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{formatDate(p.createdAt)}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => setRevokeId(p.id)} title="Revoke">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      {revoked.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Revoked ({revoked.length})
          </Typography>
          <Paper variant="outlined" sx={{ p: 1, opacity: 0.7 }}>
            {revoked.map(p => (
              <Box key={p.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, px: 1 }}>
                <Typography variant="body2">{p.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Revoked {formatDate(p.revokedAt)}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Box>
      )}

      {/* Help block: how to use */}
      <Paper sx={{ mt: 4, p: 2.5, bgcolor: isDark ? alpha('#6366f1', 0.08) : '#f5f7ff' }} variant="outlined">
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          <SyncIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
          Set up DriveX-Sync (desktop client)
        </Typography>
        <Typography variant="body2" component="div" sx={{ ml: 2 }}>
          1. <a href="/download/DriveX-Setup.exe" download>Download DriveX-Sync for Windows</a><br />
          2. Run installer (it bundles WinFsp + rclone)<br />
          3. When prompted, paste your email + the app-password you created above<br />
          4. Your DriveX appears as <code>Z:\</code> drive — drag &amp; drop like OneDrive.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Server: <code>https://mail.cruvzmail.com/api/drivex/webdav/</code> · TLS only · ClamAV-scanned
        </Typography>
      </Paper>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New app password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="normal"
            label="Label (where will you use it?)"
            placeholder="e.g. My laptop, Office desktop, Outlook on phone"
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            inputProps={{ maxLength: 80 }}
          />
          <TextField
            select
            fullWidth
            margin="normal"
            label="Scope (which protocols can use this password)"
            value={newScope}
            onChange={e => setNewScope(e.target.value)}
          >
            <MenuItem value="webdav">DriveX / WebDAV (recommended for DriveX-Sync)</MenuItem>
            <MenuItem value="imap">IMAP only</MenuItem>
            <MenuItem value="smtp">SMTP only</MenuItem>
            <MenuItem value="all">All protocols (less secure)</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Show password ONCE */}
      <Dialog open={!!createdPassword} onClose={() => setCreatedPassword(null)} fullWidth maxWidth="sm">
        <DialogTitle>App password for "{createdPassword?.label}"</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this password now. It will <strong>not</strong> be shown again.
          </Alert>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: isDark ? '#1a1a1a' : '#f8f9fa' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography sx={{ fontFamily: 'monospace', fontSize: 18, letterSpacing: 1 }}>
                {createdPassword?.password}
              </Typography>
              <IconButton onClick={() => copyToClipboard(createdPassword!.password)} title="Copy">
                <CopyIcon />
              </IconButton>
            </Stack>
          </Paper>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Use as the password when configuring DriveX-Sync, IMAP/SMTP, or WebDAV. Username is your email address.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatedPassword(null)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Revoke confirm */}
      <Dialog open={!!revokeId} onClose={() => setRevokeId(null)}>
        <DialogTitle>Revoke this app password?</DialogTitle>
        <DialogContent>
          <Typography>Any device using this password will lose access immediately.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeId(null)}>Cancel</Button>
          <Button color="error" onClick={() => handleRevoke(revokeId!)}>Revoke</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnack(null)} severity={snack?.severity || 'info'}>
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AppPasswordsSettings;
